<script setup>
import { useHead } from "@vueuse/head";
import RealtimeKujibikiCta from "@/components/molecules/RealtimeKujibikiCta.vue";

// SEO構造化データ（Article スキーマ）
const articleSchema = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "リアルタイムくじ引きとは？概要・メリット・従来のオンラインくじとの違いを解説",
  description:
    "リアルタイムくじ引きって何？従来のオンラインくじと違うの？概要・参加のメリットをわかりやすく解説。",
  datePublished: "2025-10-28",
  dateModified: "2025-11-13",
  author: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  publisher: {
    "@type": "Organization",
    name: "SukiSukiらいぶすてーしょん",
  },
  image: "/img/airticlepic/articlerealtime1.webp",
};

// メタ情報と構造化データを設定
useHead({
  title:
    "リアルタイムくじ引きとは？概要・メリット・従来のオンラインくじとの違いを解説 | SukiSukiらいぶすてーしょん",
  meta: [
    {
      name: "description",
      content:
        "リアルタイムくじ引きって何？従来のオンラインくじと違うの？概要・参加のメリットをわかりやすく解説。",
    },
    {
      property: "og:title",
      content:
        "リアルタイムくじ引きとは？概要・メリット・従来のオンラインくじとの違いを解説 | SukiSukiらいぶすてーしょん",
    },
    {
      property: "og:description",
      content:
        "リアルタイムくじ引きって何？従来のオンラインくじと違うの？概要・参加のメリットをわかりやすく解説。",
    },
    {
      property: "og:type",
      content: "website",
    },
    {
      property: "og:image",
      content: "https://sukisuki.live/img/airticlepic/articlerealtime1.webp",
    },
  ],
  script: [
    {
      type: "application/ld+json",
      children: JSON.stringify(articleSchema),
    },
  ],
});
</script>

<template>
  <v-app>
    <!-- ヒーローセクション -->
    <figure class="hero-section">
      <img
        src="/img/airticlepic/articlerealtime1.webp"
        alt="リアルタイムくじ引きの概要を解説するヒーロー画像"
      />
    </figure>

    <!-- パンくずナビゲーション -->
    <nav aria-label="パンくずリスト" class="breadcrumb-section">
      <v-container>
        <ol class="breadcrumb">
          <li><a href="/academy">TOP</a></li>
          <li>リアルタイムくじ引き</li>
        </ol>
      </v-container>
    </nav>

    <!-- メインコンテンツ -->
    <main>
      <article class="article-content">
        <v-container>
          <!-- 記事タイトル -->
          <div class="article-title-box">
            <h1>リアルタイムくじ引きとは？概要・メリット・従来のオンラインくじとの違いを解説</h1>
          </div>

          <!-- 更新日 -->
          <div class="update-date-box">
            <time datetime="2025-11-13">更新日:2025年11月13日</time>
          </div>

          <!-- イントロダクション -->
          <div class="intro-box">
            <p>
              リアルタイムくじ引きって何？従来のオンラインくじと違うの？概要・参加のメリットをわかりやすく解説。
            </p>
          </div>

          <section class="learn-section">
            <h2 class="learn-title">この記事でわかる内容</h2>
            <div class="learn-content">
              <p>
                「リアルタイムくじ引き」という言葉を耳にしたことはありますか？
                グッズのくじ引きとライブ配信を掛け合わせた新しいグッズ販売の形として、“その場でくじを引ける”イベントがクリエイター・ライバー・VTuberの間で注目を集めています。
              </p>
              <p>
                この記事では、リアルタイムくじ引きの基本的な仕組みから、通常のオンラインくじとの違い、そしてメリットまでをわかりやすく解説します。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">リアルタイムくじ引きとは？</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">配信と同時に楽しめる“ライブ連動型のくじ引き”</span>
              </p>
              <p>
                リアルタイムくじ引きとは、ライブ配信中にファンがその場で参加できるオンラインくじのこと。
                配信を見ながら購入・抽選・当選の発表が行われ、結果をみんなで共有できるのが特徴です。
              </p>
              <p>
                「SukiSukiらいぶすてーしょん」では、ライブ配信と同時にくじ引きを開催し、購入者の名前（ニックネーム）や当選結果がリアルタイムで画面に表示されます。配信者はファンにその場でお礼を言ったり、当選の瞬間を一緒に喜んだりできます。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">リアルタイムくじ引きが選ばれる4つの理由</h2>
            <div class="learn-content">
              <p>
                <span class="bold-text">POINT1｜いま応援してくれた“ファン”へすぐにお礼を</span>
              </p>
              <p>
                配信中に購入者の名前（ニックネーム）がリアルタイムで表示され、その場で「ありがとう！」と感謝を伝えられます。
              </p>
              <p>
                ファンは“推しに反応がもらえる”特別な体験を味わえ、配信者にとっても、応援の気持ちをその瞬間に返せる仕組みです。
              </p>
              <p>
                <span class="bold-text">POINT2｜当たった瞬間、配信が沸く！</span>
              </p>
              <p>
                くじ引きの結果がその場で表示されるため、「当たった！」「おめでとう！」といったコメントが配信全体を盛り上げます。
              </p>
              <p>
                静的な「購入→結果確認」ではなく、リアルタイムで全員が一緒に盛り上がるのがこの形式の最大の魅力。ライブ配信ならではの“熱量”が、購入意欲やリピートにもつながります。
              </p>
              <p>
                <span class="bold-text">POINT3｜“配信ならでは”の販売体験</span>
              </p>
              <p>
                リアルタイムくじ引きは、単なるグッズ販売ではなく演出付きのイベント体験です。当選発表の演出や、配信者のリアクションを交えたライブ感が、「観て楽しい」「参加して楽しい」双方向のエンタメを生み出します。
              </p>
              <p>
                従来のオンラインくじが「結果を確認するだけ」だったのに対し、リアルタイムくじ引きは「グッズ販売がイベント」になります。
              </p>
              <p>
                <span class="bold-text">POINT4｜面倒なことはすべておまかせ！</span>
              </p>
              <p>SukiSukiらいぶすてーしょんでは、グッズのデザインから製造・発送まで完全代行。</p>
              <p>
                配信者は企画や配信に集中するだけで、プロ品質のグッズを販売できます。制作費も無料で、準備するのは“デザイン素材のみ”。「グッズ販売に挑戦したいけど、準備が大変そう…」という人にも安心の仕組みです。
              </p>
            </div>
          </section>

          <!-- CTA ボタン -->
          <RealtimeKujibikiCta article-id="academy-article-realtime-1" />

          <section class="learn-section">
            <h2 class="learn-title">従来のオンラインくじとの違い</h2>
            <div class="learn-content-table">
              <table class="comparison-table">
                <thead>
                  <tr>
                    <th>比較項目</th>
                    <th>従来のオンラインくじ</th>
                    <th>リアルタイムくじ引き</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>抽選のタイミング</td>
                    <td>購入後すぐ</td>
                    <td>配信中・リアルタイム</td>
                  </tr>
                  <tr>
                    <td>結果の共有</td>
                    <td>個人の画面で完結</td>
                    <td>配信内で共有・発表</td>
                  </tr>
                  <tr>
                    <td>主な目的</td>
                    <td>グッズの購入</td>
                    <td>グッズの購入&ファンと盛り上がる</td>
                  </tr>
                  <tr>
                    <td>感情体験</td>
                    <td>一人で結果を見る</td>
                    <td>全員で盛り上がる＆反応が見れる</td>
                  </tr>
                  <tr>
                    <td>拡散性</td>
                    <td>低い</td>
                    <td>高い（SNS・切り抜きなど）</td>
                  </tr>
                </tbody>
              </table>
              <p>
                従来のオンラインくじは便利でしたが、つながりや応援感を感じにくい“孤独な体験”でした。
              </p>
              <p>
                一方、リアルタイムくじ引きは「配信×くじ引き」の融合によって、<span class="bold-text"
                  >販売＝体験＝イベント</span
                >という新しい形です。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">
              SukiSukiらいぶすてーしょんが実現する「リアルタイムくじ引き」
            </h2>
            <div class="learn-content">
              <p>
                SukiSukiらいぶすてーしょんは、クリエイター・ライバー・VTuber・アイドルが“リアルタイムくじ引き”を行えるプラットフォームです。
              </p>
              <p>・イベント期間を決めて開催（例：2週間で3回配信）</p>
              <p>・デザイン～製造～発送までワンストップ対応</p>
              <p>・初期費用・デザイン費無料（素材のみご準備）</p>
              <p>・視聴者はその場でくじに参加・抽選結果を確認※配信時間外でも購入は可能です</p>
              <p>
                「観て・応援して・当てて・届く」までを一気通貫で体験できる、ライブ配信時代の<span
                  class="bold-text"
                  >新しいグッズ販売スタイル</span
                >です。
              </p>
            </div>
          </section>
          <section class="learn-section">
            <h2 class="learn-title">
              まとめ｜“推しと一緒に盛り上がるくじ引き”が新しいスタンダードに
            </h2>
            <div class="learn-content">
              <p>
                リアルタイムくじ引きは、配信の盛り上がりとファンの応援をその場で可視化する、新しいファンコミュニケーションです。
              </p>
              <p>・ファンにその場でお礼を言える</p>
              <p>・当たった瞬間に配信が沸く</p>
              <p>・グッズ販売がイベントになる</p>
              <p>・制作や発送はすべてお任せ</p>
              <p>
                「推し活」と「配信販売」を融合させたこの形式は、ファンの体験価値を最大化しながら、配信者の収益化をサポートする次世代の仕組みです。
              </p>
              <p>
                もしあなたが配信者なら、次のグッズ販売イベントで“リアルタイムくじ引き”を取り入れてみませんか？ファンと一緒に作る“特別な瞬間”を作ります。
              </p>
            </div>
          </section>
        </v-container>
      </article>
    </main>

    <!-- CTA ボタン -->
    <RealtimeKujibikiCta article-id="academy-article-realtime-1" class="mb-10" />
  </v-app>
</template>

<style scoped>
/* ヒーローセクション */
.hero-section {
  padding: 20px;
  width: 100%;
}

.hero-section img {
  width: 100%;
  height: auto;
  display: block;
}

.hero-content {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
}

.hero-main-container {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 40px;
}

.hero-left-content {
  flex: 1;
  text-align: left;
}

.hero-tag {
  background-color: #c8007f;
  color: white;
  padding: 8px 20px;
  border-radius: 4px;
  display: inline-block;
  font-size: 14px;
  font-weight: bold;
  margin-bottom: 20px;
}

.hero-title {
  font-size: 2.5rem;
  color: #6b4c93;
  font-weight: bold;
  margin: 0 0 10px 0;
}

.hero-subtitle {
  font-size: 1.2rem;
  color: #6b4c93;
  margin: 0;
}

.hero-right-content {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
}

.hero-image {
  max-width: 100%;
  height: auto;
}

/* パンくず */
.breadcrumb-section {
  background-color: white;
  /* padding: 15px 0; */
}

.breadcrumb {
  color: #666;
  font-size: 14px;
  padding-left: 20px;
  padding-right: 20px;
  padding-top: 10px;
  padding-bottom: 8px;
  text-align: left;
  display: flex;
  list-style: none;
  margin: 0;
  gap: 8px;
}

.breadcrumb li {
  display: inline-block;
}

.breadcrumb li:not(:last-child)::after {
  content: ">";
  margin-left: 8px;
}

.breadcrumb a {
  color: #666;
  text-decoration: none;
}

.breadcrumb a:hover {
  text-decoration: underline;
}

/* メインコンテンツ */
::v-deep(.v-container) {
  text-align: left;
  padding: 0 !important;
}

.article-content {
  /* margin: 40px 0; */
  text-align: left;
}

.article-content h2,
.article-content h3 {
  text-align: left;
}

.article-title-box {
  margin-bottom: 5px;
}

.article-title-box h1,
.article-title-box h2 {
  color: #333;
  font-size: 18px;
  margin: 0;
  font-weight: bold;
  text-align: left;
  padding-left: 20px;
  padding-right: 20px;
  line-height: 1.6;
}

.update-date-box {
  padding: 10px 20px;
  margin-bottom: 10px;
}

.update-date-box p,
.update-date-box time {
  color: #333;
  margin: 0;
  font-size: 14px;
  text-align: left;
}

.intro-box {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 30px;
  margin-left: 20px;
  margin-right: 20px;
  font-size: 14px;
}

.intro-box p {
  color: #333;
  margin: 0;
  line-height: 1.6;
  text-align: left;
}

.learn-section {
  margin: 30px 0;
}

.learn-title {
  color: #333;
  font-size: 20px;
  font-weight: bold;
  margin: 0 0 20px 20px;
  text-align: left;
  background-color: #ffe0e0;
  padding: 15px 0 15px 20px;
  position: relative;
  line-height: 1.6;
}

.learn-title::before {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 8px;
  height: 100%;
  background-color: #982da6;
}

.learn-content {
  margin-left: 20px;
  text-align: left;
}

.learn-content p {
  color: #333;
  line-height: 1.6;
  margin: 0 20px 15px 0;
  text-align: left;
  font-size: 16px;
}

.custom-content {
  line-height: 1.6;
}

.bold-text {
  font-weight: bold;
}

/* 比較テーブル */
.comparison-table {
  width: 100%;
  border-collapse: collapse;
  margin: 20px 0;
  font-size: 14px;
}

.comparison-table th,
.comparison-table td {
  padding: 12px;
  text-align: left;
  border: 1px solid #ddd;
}

.comparison-table th {
  background-color: #ffe0e0;
  font-weight: bold;
  color: #333;
}

.comparison-table td {
  background-color: #fff;
}

.comparison-table tr:hover {
  background-color: #f9f9f9;
}

.learn-content-table {
  margin-left: 20px;
  margin-right: 20px;
  text-align: left;
  line-height: 1.6;
}

@media (min-width: 750px) {
  .hero-section {
    padding: 0 25%;
  }

  .hero-section img {
    width: 100%;
    height: auto;
    display: block;
  }
  .breadcrumb {
    padding: 10px 25%;
  }
  .v-container {
    max-width: 5000px !important;
  }
  .article-title-box {
    padding: 0 25%;
  }
  .article-title-box h1,
  .article-title-box h2 {
    font-size: 20px;
    padding: 0;
    line-height: 1.6;
  }
  .update-date-box {
    padding: 10px 25%;
  }
  .update-date-box p,
  .update-date-box time {
    padding: 0;
  }
  .intro-box {
    padding: 10px;
  }
  .intro-box p {
    font-size: 16px;
    line-height: 1.6;
  }
  .intro-box {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-title {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content {
    margin-left: 25%;
    margin-right: 25%;
  }
  .learn-content p {
    margin-left: 0;
    margin-right: 0;
  }
  .learn-content-table {
    margin-left: 25%;
    margin-right: 25%;
    text-align: left;
  }
}
</style>
